import { DateTime } from "luxon";
import { useStyles } from "./style";
import clsx from "clsx";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
	SBBox,
	SBDatePicker,
	SBIcon,
	SBIconButton,
	SBMenu,
} from "modules/Commons";
import { DateRangeType } from "modules/Commons/SBDate/models/dateRangeType";
import { useTranslation } from "react-i18next";
import { IDatePicker } from "domain/Utils/models/IDatePicker";
import { useSystemFilterContext } from "contexts/SystemFilterContext";
import { EditingSide } from "./models/editingSide";

export interface IDatePickerMenuRef {
	close: () => void;
}

export interface DatePickerMenuProps {
	date?: DateTime;
	startDate?: DateTime;
	endDate?: DateTime;
	minDate?: DateTime;
	maxDate?: DateTime;
	availableDates?: string[];
	onChange?: (value: IDatePicker) => void;
	setDateRangeType?: (value: DateRangeType) => void;
	dateRangeType?: DateRangeType;
	fixedStart?: boolean;
	range?: boolean;
	showConfigOptions?: boolean;
	showDateGranularityOptions?: boolean;
	useConfigMenuStyles?: boolean;
	solo?: boolean;
}

interface IMenuRef {
	close: () => void;
}

const useEditingState = () => {
	const [isEditing, setIsEditing] = useState<EditingSide | null>(null);
	const [inputValue, setInputValue] = useState("");

	const startEditing = useCallback((initialValue: string) => {
		setInputValue(initialValue);
		setIsEditing(EditingSide.Start);
	}, []);

	const endEditing = useCallback((initialValue: string) => {
		setInputValue(initialValue);
		setIsEditing(EditingSide.End);

	}, []);

	const stopEditing = useCallback(() => {
		setIsEditing(null);
		setInputValue("");
	}, []);

	return { isEditing, inputValue, setInputValue, startEditing, endEditing, stopEditing };
};

export default forwardRef<IDatePickerMenuRef, DatePickerMenuProps>(
	(
		{
			date,
			startDate,
			endDate,
			minDate,
			maxDate,
			availableDates,
			onChange,
			setDateRangeType = () => { },
			dateRangeType = DateRangeType.Daily,
			fixedStart = false,
			range = true,
			showConfigOptions = true,
			showDateGranularityOptions = true,
			useConfigMenuStyles = false,
			solo,
		},
		ref
	) => {
		const { t } = useTranslation();
		const classes = useStyles();
		const menuRef = useRef<IMenuRef>(null);
		const inputRef = useRef<HTMLInputElement>(null);
		const { context } = useSystemFilterContext();
		const [selectedDate, setSelectedDate] = useState<DateTime | undefined>(date);
		const isFirstUpdate = useRef(true);

		const { isEditing, inputValue, setInputValue, startEditing, endEditing, stopEditing } = useEditingState();

		const dateFormat = t("dateFormat");

		useImperativeHandle(ref, () => ({
			close: () => menuRef.current?.close(),
		}));

		const handleOnApply = useCallback((value: IDatePicker) => {
			setSelectedDate(value.date);
			menuRef.current?.close();
			onChange?.(value);
		}, [onChange]);

		useEffect(() => {
			if (isFirstUpdate.current) {
				isFirstUpdate.current = false;
				return;
			}
			if (!solo) setSelectedDate(context);
		}, [context, solo]);

		useEffect(() => {
			if (isEditing && inputRef.current) {
				inputRef.current.focus();
				inputRef.current.select();
			}
		}, [isEditing]);

		const formatDate = useCallback((dt?: DateTime) => {
			if (!dt?.isValid) return "";
			return dt.toFormat(dateFormat);
		}, [dateFormat]);

		const tryParseDate = useCallback((value: string): DateTime | null => {
			if (!value?.trim()) return null;

			const cleanValue = value.trim();

			let parsed = DateTime.fromFormat(cleanValue, dateFormat);

			if (!parsed.isValid && /^\d{8}$/.test(cleanValue)) {
				parsed = DateTime.fromFormat(cleanValue, "ddMMyyyy");
			}

			if (!parsed.isValid) {
				const fallbackFormats = ["d/M/yyyy", "d/M/yy", "dd/M/yyyy", "d/MM/yyyy"];
				for (const fmt of fallbackFormats) {
					parsed = DateTime.fromFormat(cleanValue, fmt);
					if (parsed.isValid) break;
				}
			}

			return parsed.isValid ? parsed : null;
		}, [dateFormat]);

		const isDateAvailable = useCallback((date: DateTime): boolean => {
			if (minDate && date < minDate.startOf("day")) return false;
			if (maxDate && date > maxDate.startOf("day")) return false;
			if (availableDates?.length) {
				return availableDates.includes(date.toISODate());
			}
			return true;
		}, [minDate, maxDate, availableDates]);

		const handleDateBoxClick = useCallback((side: EditingSide) => {
			if (side === EditingSide.Start && fixedStart) return;

			const currentDate = side === EditingSide.Start
				? (range ? startDate : selectedDate)
				: endDate;
			const value = formatDate(currentDate);

			if (side === EditingSide.Start) startEditing(value);
			else endEditing(value);
		}, [range, startDate, selectedDate, endDate, formatDate, startEditing, endEditing, fixedStart]);



		const confirmDate = useCallback(() => {
			const parsed = tryParseDate(inputValue);

			if (parsed && isDateAvailable(parsed)) {
				if (range) {
					if (isEditing === EditingSide.Start && endDate && parsed > endDate) {
						stopEditing();
						return;
					}
					if (isEditing === EditingSide.End && startDate && parsed < startDate) {
						stopEditing();
						return;
					}
				}

				stopEditing();

				if (isEditing === EditingSide.Start) {
					if (range) {
						onChange?.({
							date: undefined,
							range: { startDate: parsed, endDate },
							type: dateRangeType,
						});
					} else {
						onChange?.({
							date: parsed,
							range: undefined,
							type: dateRangeType,
						});
						setSelectedDate(parsed);
					}
				} else {
					onChange?.({
						date: undefined,
						range: { startDate, endDate: parsed },
						type: dateRangeType,
					});
				}
			} else {
				stopEditing();
			}
		}, [inputValue, tryParseDate, isDateAvailable, isEditing, range, endDate, startDate, dateRangeType, onChange, stopEditing]);


		const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault();
				confirmDate();
			} else if (e.key === "Escape") {
				e.preventDefault();
				stopEditing();
			}
		}, [confirmDate, stopEditing]);

		const getDisplayDate = (side: EditingSide): string => {
			if (side === EditingSide.Start) {

				if (range) return (startDate?.toFormat(dateFormat)) || "-";
				return (selectedDate?.toFormat(dateFormat)) || "-";
			}
			return (endDate?.toFormat(dateFormat)) || "-";
		};

		const renderDateBox = (side: EditingSide) => {
			const isActive = isEditing === side;
			const boxClass = side === EditingSide.Start ? classes.sbLeftDateBox : classes.sbRightDateBox;


			if (isActive) {
				return (
					<input
						ref={inputRef}
						type="text"
						size={1}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onBlur={confirmDate}
						onKeyDown={handleKeyDown}
						placeholder={dateFormat}
						className={clsx(
							useConfigMenuStyles ? classes.sbConfigMenuDateBox : classes.sbDateBox,
							boxClass,
							classes.dateInput
						)}
					/>
				);
			}

			return (
				<SBBox
					className={clsx(useConfigMenuStyles ? classes.sbConfigMenuDateBox : classes.sbDateBox, boxClass)}
					onClick={() => handleDateBoxClick(side)}
					style={{ cursor: "text" }}
				>
					{getDisplayDate(side)}
				</SBBox>
			);
		};

		return (
			<SBBox display="flex" height="100%">
				<SBMenu
					ref={menuRef}
					canOpen
					action={
						<div className={clsx(useConfigMenuStyles ? classes.sbConfigMenuIconDateBox : classes.sbIconDateBox)}>
							<SBIconButton>
								<SBIcon
									icon="calendar"
									color="#828282"
									size={17}
								/>
							</SBIconButton>
						</div>
					}
					content={
						<SBDatePicker
							fixedStart={fixedStart}
							range={range}
							showConfigOptions={showConfigOptions}
							showDateGranularityOptions={showDateGranularityOptions}
							defaultValue={{ date: selectedDate, startDate, endDate }}
							onApply={handleOnApply}
							onChangeType={setDateRangeType}
							minDate={minDate}
							maxDate={maxDate}
							availableDates={availableDates}
							type={dateRangeType}
						/>
					}
				/>
				{renderDateBox(EditingSide.Start)}
				{range && renderDateBox(EditingSide.End)}

			</SBBox>
		);
	}
);
